// src/components/dashboard/ProductManagement.js
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Edit, Trash2, Eye, EyeOff, Filter, Download, Upload, 
  Search, X, Package, Tag, DollarSign, Hash, Image, Grid, List,
  AlertCircle, Check, GripVertical, ArrowUpDown, TrendingUp, Sparkles
} from 'lucide-react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

function ProductManagement({ products, updateProducts, fetchProducts, isMobile }) {
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    weight: '',
    category: 'Premium Cuts',
    image: '',
    isWholesale: false,
    isActive: true,
    id: 0
  });
  const [imageFile, setImageFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const categories = ['Premium Cuts', 'BBQ Specials', 'Traditional Cuts', 'Wholesale'];

  // Filter and search products
  const filteredProducts = products.filter(product => {
    const matchesSearch = searchQuery === '' || 
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.category.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesFilters = Object.keys(filters).every(key => {
      if (!filters[key]) return true;
      return String(product[key]).toLowerCase().includes(filters[key].toLowerCase());
    });

    return matchesSearch && matchesFilters;
  });

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProductForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setProductForm(prev => ({ ...prev, image: previewUrl }));
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    setIsUploading(true);

    try {
      let finalImageUrl = productForm.image;

      if (imageFile) {
        const formData = new FormData();
        formData.append('image', imageFile);
        formData.append('apiType', 'submitProduct');
        formData.append('name', productForm.name);
        formData.append('description', productForm.description);
        formData.append('price', parseFloat(productForm.price));
        formData.append('weight', productForm.weight);
        formData.append('category', productForm.category);
        formData.append('isWholesale', productForm.isWholesale ? 1 : 0);
        formData.append('isActive', 1);

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/index.php`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        if (response.data.success) {
          await fetchProducts();
          resetForm();
          alert('Product added successfully!');
        }
      } else {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/index.php`,
          {
            apiType: "submitProduct",
            name: productForm.name,
            description: productForm.description,
            price: parseFloat(productForm.price),
            weight: productForm.weight,
            category: productForm.category,
            image: finalImageUrl,
            isWholesale: productForm.isWholesale ? 1 : 0,
            isActive: 1
          }
        );

        if (response.data.success) {
          await fetchProducts();
          resetForm();
          alert('Product added successfully!');
        }
      }
    } catch (error) {
      console.error("Error adding product:", error);
      alert("Error adding product. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price,
      weight: product.weight,
      category: product.category,
      image: product.image,
      isWholesale: product.isWholesale,
      isActive: product.isActive,
      id: product.id
    });
    setImageFile(null);
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    setIsUploading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/index.php`,
        {
          apiType: "submitEditProduct",
          ...productForm,
          id: editingProduct.id
        }
      );

      if (response.data.success) {
        await fetchProducts();
        resetForm();
        alert('Product updated successfully!');
      }
    } catch (error) {
      console.error("Error updating product:", error);
      alert("Error updating product. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeactivate = async (productId, isActive) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/index.php`, {
        apiType: isActive ? "deactivateProduct" : "activateProduct",
        productId
      });

      if (response.data.success) {
        await fetchProducts();
        alert(`Product ${isActive ? 'deactivated' : 'activated'} successfully!`);
      }
    } catch (error) {
      console.error("Error updating product status:", error);
      alert("Failed to update product status.");
    }
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
      try {
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/index.php`, {
          apiType: "deleteProduct",
          productId
        });

        if (response.data.success) {
          await fetchProducts();
          alert('Product deleted successfully!');
        }
      } catch (error) {
        console.error('Delete error:', error);
        alert('Failed to delete product.');
      }
    }
  };

  const handleExport = (type) => {
    try {
      const exportData = filteredProducts.map((product, index) => ({
        'SN': index + 1,
        'Name': product.name,
        'Description': product.description,
        'Price': `$${product.price}`,
        'Weight': product.weight,
        'Category': product.category,
        'Wholesale': product.isWholesale ? 'Yes' : 'No',
        'Status': product.isActive ? 'Active' : 'Inactive'
      }));

      if (type === 'excel') {
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Products");
        XLSX.writeFile(wb, `products_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      } else if (type === 'pdf') {
        const doc = new jsPDF();
        
        doc.setFontSize(16);
        doc.text('Products Export', 14, 15);
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 22);
        
        doc.autoTable({
          startY: 25,
          head: [['SN', 'Name', 'Price', 'Weight', 'Category', 'Status']],
          body: exportData.map(item => [item.SN, item.Name, item.Price, item.Weight, item.Category, item.Status]),
          theme: 'grid',
          headStyles: { fillColor: [26, 26, 26] }
        });

        doc.save(`products_export_${new Date().toISOString().split('T')[0]}.pdf`);
      }
      
      alert(`Products exported successfully as ${type.toUpperCase()}!`);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export products.');
    }
  };

  const resetForm = () => {
    setIsAddingProduct(false);
    setEditingProduct(null);
    setProductForm({
      name: '',
      description: '',
      price: '',
      weight: '',
      category: 'Premium Cuts',
      image: '',
      isWholesale: false,
      isActive: true
    });
    setImageFile(null);
  };

  const stats = {
    total: products.length,
    active: products.filter(p => p.isActive).length,
    wholesale: products.filter(p => p.isWholesale).length,
    inactive: products.filter(p => !p.isActive).length
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
            Product Management
          </h2>
          <p className="text-gray-400">Manage your butchery's product catalog</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsAddingProduct(true)}
            className="px-4 py-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl font-bold hover:shadow-lg transition-all flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Add Product</span>
          </motion.button>
          
          <div className="flex bg-gray-800 p-1 rounded-lg">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-gray-700' : ''}`}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-gray-700' : ''}`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="relative">
          <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-2xl blur-xl"></div>
          <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 flex items-center justify-center">
                <Package className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-sm text-gray-400">Total Products</div>
                <div className="text-2xl font-bold text-white">{stats.total}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-1 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-2xl blur-xl"></div>
          <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 flex items-center justify-center">
                <Check className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-sm text-gray-400">Active</div>
                <div className="text-2xl font-bold text-white">{stats.active}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-2xl blur-xl"></div>
          <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-600 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-sm text-gray-400">Wholesale</div>
                <div className="text-2xl font-bold text-white">{stats.wholesale}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-2xl blur-xl"></div>
          <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-sm text-gray-400">Inactive</div>
                <div className="text-2xl font-bold text-white">{stats.inactive}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="relative">
        <div className="absolute -inset-1 bg-gradient-to-r from-gray-800/20 to-gray-900/20 rounded-3xl blur-xl"></div>
        <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search products by name, description, or category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
            
            <div className="flex items-center gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                onClick={() => setShowFilters(!showFilters)}
                className="px-4 py-3 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-xl flex items-center space-x-2 transition-colors"
              >
                <Filter className="w-5 h-5" />
                <span>Filters</span>
              </motion.button>

              <div className="flex space-x-2">
                <button
                  onClick={() => handleExport('excel')}
                  className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg flex items-center space-x-2"
                >
                  <Download className="w-4 h-4" />
                  <span>Excel</span>
                </button>
                <button
                  onClick={() => handleExport('pdf')}
                  className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-lg flex items-center space-x-2"
                >
                  <Download className="w-4 h-4" />
                  <span>PDF</span>
                </button>
              </div>
            </div>
          </div>

          {/* Filter Panel */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-800/30 rounded-lg mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Category</label>
                    <select
                      value={filters.category || ''}
                      onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    >
                      <option value="">All Categories</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Status</label>
                    <select
                      value={filters.isActive || ''}
                      onChange={(e) => setFilters({ ...filters, isActive: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    >
                      <option value="">All Status</option>
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Wholesale</label>
                    <select
                      value={filters.isWholesale || ''}
                      onChange={(e) => setFilters({ ...filters, isWholesale: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    >
                      <option value="">All Types</option>
                      <option value="true">Wholesale</option>
                      <option value="false">Retail</option>
                    </select>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Products Grid/List */}
      {filteredProducts.length > 0 ? (
        viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onEdit={handleEditProduct}
                onDeactivate={handleDeactivate}
                onDelete={handleDelete}
                isMobile={isMobile}
              />
            ))}
          </div>
        ) : (
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-gray-800/10 to-gray-900/10 rounded-3xl blur-xl"></div>
            <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-800/50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-400 uppercase">Product</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-400 uppercase">Price</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-400 uppercase">Category</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-400 uppercase">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-400 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {paginatedProducts.map((product) => (
                      <tr key={product.id} className="hover:bg-gray-800/30 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-4">
                            <img src={product.image} alt={product.name} className="w-12 h-12 rounded-lg object-cover" />
                            <div>
                              <div className="font-bold text-white">{product.name}</div>
                              <div className="text-sm text-gray-400">{product.description.substring(0, 50)}...</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-bold text-emerald-400">${product.price}</div>
                          <div className="text-sm text-gray-500">{product.weight}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-3 py-1 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-blue-400 text-xs rounded-full">
                            {product.category}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs ${product.isActive ? 'bg-gradient-to-r from-emerald-500/20 to-green-500/20 text-emerald-400' : 'bg-gradient-to-r from-red-500/20 to-pink-500/20 text-red-400'}`}>
                            {product.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEditProduct(product)}
                              className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeactivate(product.id, product.isActive)}
                              className="p-2 text-amber-400 hover:bg-amber-500/10 rounded-lg transition-colors"
                            >
                              {product.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => handleDelete(product.id)}
                              className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )
      ) : (
        <div className="relative text-center py-16">
          <div className="absolute -inset-1 bg-gradient-to-r from-gray-800/10 to-gray-900/10 rounded-3xl blur-xl"></div>
          <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-12">
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-full flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-emerald-500" />
            </div>
            <h4 className="text-xl font-bold text-white mb-2">No products found</h4>
            <p className="text-gray-400 mb-6">Try adjusting your search or filters</p>
            <button
              onClick={() => setIsAddingProduct(true)}
              className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl font-bold hover:shadow-lg transition-all"
            >
              Add Your First Product
            </button>
          </div>
        </div>
      )}

      {/* Pagination */}
      {filteredProducts.length > 0 && (
        <div className="flex flex-col md:flex-row items-center justify-between px-6 py-4 bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800">
          <div className="text-sm text-gray-400 mb-4 md:mb-0">
            Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredProducts.length)} of {filteredProducts.length} products
          </div>
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-gray-800 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm font-medium text-white">
              Page {currentPage} of {totalPages}
            </span>
            <button 
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-gray-800 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit Product Modal */}
      <AnimatePresence>
        {(isAddingProduct || editingProduct) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="p-8">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 flex items-center justify-center">
                      <Package className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white">
                      {editingProduct ? 'Edit Product' : 'Add New Product'}
                    </h3>
                  </div>
                  <button 
                    onClick={resetForm} 
                    className="p-2 hover:bg-gray-800 rounded-full transition-colors"
                  >
                    <X className="w-6 h-6 text-gray-400" />
                  </button>
                </div>

                <form onSubmit={editingProduct ? handleUpdateProduct : handleAddProduct}>
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">Product Name</label>
                        <div className="relative">
                          <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                          <input
                            type="text"
                            name="name"
                            value={productForm.name}
                            onChange={handleInputChange}
                            required
                            className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-emerald-500"
                            placeholder="Enter product name"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">Price ($ per kg)</label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                          <input
                            type="number"
                            name="price"
                            value={productForm.price}
                            onChange={handleInputChange}
                            required
                            step="0.01"
                            className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-emerald-500"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Description</label>
                      <textarea
                        name="description"
                        value={productForm.description}
                        onChange={handleInputChange}
                        required
                        rows="3"
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-emerald-500"
                        placeholder="Enter product description"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">Weight/Unit</label>
                        <div className="relative">
                          <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                          <input
                            type="text"
                            name="weight"
                            value={productForm.weight}
                            onChange={handleInputChange}
                            required
                            className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-emerald-500"
                            placeholder="e.g., 1kg, 10kg+"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">Category</label>
                        <select
                          name="category"
                          value={productForm.category}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-emerald-500"
                        >
                          {categories.map(category => (
                            <option key={category} value={category}>{category}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Product Image</label>
                      <div className="space-y-4">
                        {productForm.image && (
                          <div className="relative w-32 h-32 rounded-xl overflow-hidden border border-gray-700">
                            <img src={productForm.image} alt="Preview" className="w-full h-full object-cover" />
                          </div>
                        )}
                        <div className="relative">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageFileChange}
                            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-emerald-600 file:text-white hover:file:bg-emerald-700"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-6">
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <div className="relative">
                          <input
                            type="checkbox"
                            name="isWholesale"
                            checked={productForm.isWholesale}
                            onChange={handleInputChange}
                            className="sr-only"
                          />
                          <div className={`w-6 h-6 rounded border ${productForm.isWholesale ? 'bg-emerald-600 border-emerald-600' : 'bg-gray-700 border-gray-600'}`}>
                            {productForm.isWholesale && <Check className="w-4 h-4 text-white m-auto" />}
                          </div>
                        </div>
                        <span className="text-gray-300">Wholesale Product</span>
                      </label>
                      
                      {editingProduct && (
                        <label className="flex items-center space-x-3 cursor-pointer">
                          <div className="relative">
                            <input
                              type="checkbox"
                              name="isActive"
                              checked={productForm.isActive}
                              onChange={handleInputChange}
                              className="sr-only"
                            />
                            <div className={`w-6 h-6 rounded border ${productForm.isActive ? 'bg-emerald-600 border-emerald-600' : 'bg-gray-700 border-gray-600'}`}>
                              {productForm.isActive && <Check className="w-4 h-4 text-white m-auto" />}
                            </div>
                          </div>
                          <span className="text-gray-300">Active Product</span>
                        </label>
                      )}
                    </div>
                  </div>

                  <div className="flex space-x-4 mt-8">
                    <button
                      type="submit"
                      disabled={isUploading}
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl font-bold hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
                    >
                      {isUploading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <>
                          {editingProduct ? 'Update Product' : 'Add Product'}
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={resetForm}
                      className="flex-1 px-6 py-3 bg-gray-800 border border-gray-700 text-gray-300 rounded-xl font-bold hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ProductCard({ product, onEdit, onDeactivate, onDelete, isMobile }) {
  return (
    <motion.div
      whileHover={{ y: -5 }}
      className="relative group"
    >
      <div className="absolute -inset-0.5 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-2xl blur opacity-0 group-hover:opacity-100 transition duration-500"></div>
      <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 overflow-hidden">
        <div className="relative h-48 overflow-hidden">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
          <div className="absolute top-4 left-4 flex flex-col gap-2">
            {product.isWholesale && (
              <span className="px-3 py-1 bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-xs font-bold rounded-full">
                WHOLESALE
              </span>
            )}
            <span className={`px-3 py-1 text-xs font-bold rounded-full ${product.isActive ? 'bg-gradient-to-r from-emerald-600/80 to-green-600/80 text-white' : 'bg-gradient-to-r from-red-600/80 to-pink-600/80 text-white'}`}>
              {product.isActive ? 'ACTIVE' : 'INACTIVE'}
            </span>
          </div>
        </div>

        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-white mb-2">{product.name}</h3>
              <p className="text-sm text-gray-400 line-clamp-2">{product.description}</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-emerald-400">${product.price}</div>
              <div className="text-sm text-gray-500">{product.weight}</div>
            </div>
          </div>

          <div className="flex items-center justify-between mt-6">
            <span className="px-3 py-1 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-blue-400 text-sm rounded-full">
              {product.category}
            </span>
            
            <div className="flex space-x-2">
              <button
                onClick={() => onEdit(product)}
                className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                title="Edit"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDeactivate(product.id, product.isActive)}
                className="p-2 text-amber-400 hover:bg-amber-500/10 rounded-lg transition-colors"
                title={product.isActive ? 'Deactivate' : 'Activate'}
              >
                {product.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button
                onClick={() => onDelete(product.id)}
                className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default ProductManagement;